import { c as _c } from "react/compiler-runtime";
import { type RefObject, createContext, useContext, useMemo } from "react";
import type { FilterTypeConfig } from "./types";
export interface FiltersContextValue<T extends Record<string, any>> {
  filters: T;
  enhancedFilterTypes: FilterTypeConfig[];
  filterTypes: FilterTypeConfig[];
  onFilterChange: (key: keyof T, value: any) => void;
}
export interface PanelContextValue {
  isOpen: boolean;
  searchQuery: string;
  pendingFilters: Record<string, any>;
  effectiveFilters: Record<string, any>;
  searchInputRef: RefObject<HTMLInputElement | null>;
  searchInputFocused: boolean;
}
export interface ActionsContextValue {
  setOpen: (open: boolean) => void;
  setSearchQuery: (query: string) => void;
  updatePending: (key: string, value: any) => void;
  commitImmediate: (key: string, value: any) => void;
  dropTouched: (key: string) => void;
  focusSearch: () => void;
  setSearchInputFocused: (focused: boolean) => void;
}
export const FiltersContext = createContext<FiltersContextValue<any> | null>(null);
export const PanelContext = createContext<PanelContextValue | null>(null);
export const ActionsContext = createContext<ActionsContextValue | null>(null);
export function useFiltersContext() {
  const ctx = useContext(FiltersContext) as FiltersContextValue<T> | null;
  if (!ctx) {
    throw new Error("useFiltersContext must be used inside <TableFiltersProvider>");
  }
  return ctx;
}
export function usePanelContext() {
  const ctx = useContext(PanelContext);
  if (!ctx) {
    throw new Error("usePanelContext must be used inside <TableFiltersProvider>");
  }
  return ctx;
}
export function useActionsContext() {
  const ctx = useContext(ActionsContext);
  if (!ctx) {
    throw new Error("useActionsContext must be used inside <TableFiltersProvider>");
  }
  return ctx;
}
const isEmptyValue = (v: any) => v === undefined || v === null || v === "" || Array.isArray(v) && v.length === 0;
export function useActiveFilterCount() {
  const {
    filters,
    enhancedFilterTypes
  } = useFiltersContext();
  let count = 0;
  for (const ft of enhancedFilterTypes) {
    if (!isEmptyValue(filters[ft.filterKey])) {
      count++;
    }
  }
  return count;
}
export function useFilterValue(filterKey) {
  const {
    filters
  } = useFiltersContext();
  const panel = useContext(PanelContext);
  if (panel?.isOpen) {
    return panel.effectiveFilters[filterKey];
  }
  return filters[filterKey];
}
export function useHasActiveFilter(filterKey) {
  const $ = _c(2);
  const t0 = useFilterValue(filterKey);
  let t1;
  if ($[0] !== t0) {
    t1 = isEmptyValue(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return !t1;
}